/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/**
 * A front-end service to manage access to STAC dataset info stored on ebx-core.
 * Fetches the full catalog of dataset metadata when initialised, so provides
 * an asynchronous init() method, and getMetadata() should return immediately.
 */

/* eslint-disable no-prototype-builtins */
import { coreDB } from "@/firebase.js";

let datasetsCollection;

// Service object
let service = {
    initialised: false,
    // Datasets, using the normalised id as key
    datasets: {},
    // A map of original ids to normalised id, for lookup
    datasetIdMappings: {}
};

/**
 * Given a document from the Firestore GEE metadata collection,
 * extract the data, restore its original id, then record it in
 * the service's `datasets` object.
 *
 * @param {*} doc
 * @return {*} the id with which the datset metadata was indexed in the service datasets object
 */
function addMetadataToDatasets(doc) {
    // Get the data from the doc
    let data = doc.data()
    // Set the data's id back to the original GEE id
    data.id = data.orig_id
    // Use normalised id as the key, and record a mapping to support lookups
    let normalisedId = constrainIdForFirestore(data.id)
    service.datasetIdMappings[data.id] = normalisedId
    // Use the normalised GEE catalog title/id as the lookup id
    service.datasets[normalisedId] = data
    return data.id
}

/**
 * This method converts GEE ids into the constrained format required by Firestore ids.
 * See `constrain_doc_id()` method in `google-functions/gather_stac_data/main.py`.
 * @param {*} id
 * @return {*} 
 */
function constrainIdForFirestore(id) {
    return id.replace(/\//g, '_');
}

/**
 * A service to manage access to STAC dataset info stored on ebx-core.
 * Front-end code.
 */
const DatasetService = {
    
    isInitialised: () => {
        return service.initialised;
    },

    /**
     * Initialise the service.
     * Fill the datasets object with metadata for all the datasets. This is about 2.5Mb of info.
     */
    init: async () => {
        console.info("Initialising DatasetService");
        
        datasetsCollection = coreDB.collection('datasets');
        // We get all the metadata up front
        console.debug("Loading full metadata from ebx-core");
        // Get the datasets metadata. We do not watch a snapshot for changes; the collection is only updated when the gather_stac_data function runs.

        let snapshot = await datasetsCollection.get();
        console.log("Loading datasets metadata");
        snapshot.forEach(doc => {
            addMetadataToDatasets(doc);
            // let id = addMetadataToDatasets(doc);
            // console.debug(` - Loaded ${id}`)
        });
        // console.debug(service.datasets);
        service.initialised = true;
        // console.log(service.datasets);
        
        console.info("Finished loading datasets metadata");
        console.info("DatasetService initialised");
    },

    /**
     * Get metadata block for an id. If there is no entry in the service's datasets object
     * for the supplied id, the method returns null.
     *
     * @param {*} datasetId
     * @return {*} the metadata for the given id, or null if there is no match
     */
    getMetadata(datasetId) {
        // Normalise the search id for lookup
        datasetId = constrainIdForFirestore(datasetId)
        console.debug(`Looking up metadata for dataset ${datasetId}`);
        let data = service.datasets[datasetId];
        console.debug(`Returning data ${data}`)
        return data;
    },
    
    /**
     * Get a list of all dataset titles.
     * Returns the original dataset ids.
     *
     * @return {*} 
     */
    getDatasetTitles() {
        console.debug(`Getting list of dataset titles`);
        return Object.keys(service.datasetIdMappings);
    },

    /**
     * Get datasets metadata to be used by the blocks
     *
     * @param {*} datasetId
     * @return {*} the metadata object for the given id, or null if there is no match
     * { 
     *   from: 'yyyy-mm-dd', 
     *   to: 'yyyy-mm-dd', 
     *   name: 'Hansen Global Forest Change v1.8 (2000-2020)', 
     *   scale: 30, 
     *   collectionID: 'UMD/hansen/global_forest_change_2020_v1_8', 
     *   bands: { tree_cover: 'treecover2000', loss: 'loss', gain: 'gain' } // this will need refactored
     * },
     */
    getBlockDatasetSpec(dataset) {
        // format the data id for firestore
        let formattedDataset = constrainIdForFirestore(dataset)

        let datasetsLength = Object.keys(service.datasets).length;

        if (service.datasets && datasetsLength > 0) {
            let catalogs = service.datasets;
            let candidates = this.getDatasetsFromPartialId(formattedDataset, catalogs)
            
            // get the latest dataset by ID
            let metadata = this.returnMostRecentCatalog(candidates)
            
            let catalogSpec = { 
                from: metadata.start_date, 
                to: metadata.end_date, 
                name: metadata.title, 
                scale: metadata.gsd[0], 
                collectionID: metadata.orig_id
            }
            return catalogSpec
        } else {
            return {}
        }
    },
    /**
     * Return a set of catalogs from ebx-core matching a partial id
     *
     * @param {*} id
     * @param {*} catalogs
     * @return {*} array of datasets that match the partial id 
     */
    getDatasetsFromPartialId(id, catalogs) {
        let candidates = []
        Object.keys(catalogs).forEach(key => {
            if (key.includes(id)) {
                candidates.push(catalogs[key])
            }
        })
        return candidates;
    },
    /**
     * Sort out an array of catalogs metadata
     *
     * @param {*} catalogs
     * @return {*} the most recent by end_year dataset
     */
    returnMostRecentCatalog(catalogs) {
        catalogs.sort(function (a,b){
            return parseInt(b.endyear) - parseInt(a.endyear)
        })
        return catalogs[0]
    },
};

export {
    DatasetService
}
